import { useMemo, useState } from 'react';
import { Building2, Check, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuthStore } from '@/stores/auth';

const EMPTY_ORGS: readonly string[] = Object.freeze([]);

/**
 * Lightweight org switcher placeholder. The full implementation (loading the
 * org list from the API, persisting the active org) lands with task 22.1.
 * Today this just exposes the active user's first organization id.
 */
export function OrgSwitcher() {
  // IMPORTANT: select the raw field (which may be undefined). Returning
  // `?? []` inside the selector creates a fresh array each render and
  // triggers an infinite re-render loop with zustand's default equality.
  const userOrgIds = useAuthStore((s) => s.user?.org_ids);
  const orgIds = useMemo<readonly string[]>(
    () => userOrgIds ?? EMPTY_ORGS,
    [userOrgIds],
  );
  const [activeOrg, setActiveOrg] = useState<string | undefined>(orgIds[0]);

  const trigger = (
    <Button variant="outline" size="sm" className="h-9 gap-2 px-3">
      <Building2 className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
      <span className="max-w-[160px] truncate text-sm">
        {activeOrg ? `Org · ${activeOrg.slice(0, 8)}` : 'Personal'}
      </span>
      <ChevronDown className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
    </Button>
  );

  if (orgIds.length === 0) {
    return trigger;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56">
        <DropdownMenuLabel>Organizations</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {orgIds.map((id) => (
          <DropdownMenuItem
            key={id}
            onSelect={() => setActiveOrg(id)}
            className="justify-between"
          >
            <span className="truncate">Org · {id.slice(0, 8)}</span>
            {activeOrg === id && <Check className="h-4 w-4" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
